/**
 * GoalImportModal - normalized CSV goal import.
 * State machine: upload -> preview -> resolve (only when conflicts exist) -> result.
 * Nothing is written until the resolve/preview step's primary action ("Import") runs execute.
 * Supports mode='single' (scoped to one client) and mode='multi' (onboarding, fan-out by
 * client_external_id per row — clientId is omitted).
 */

import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  UploadCloud,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  AlertTriangle,
} from 'lucide-react';
import type { GoalImportRow, GoalConflictResolution } from '@upskill/shared';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { ResponsiveTableWrapper } from '../../components/ui';
import { useGoalImportValidate, useGoalImportExecute } from '../../hooks/useGoals';
import {
  DropZone,
  DropLabel,
  DropSubLabel,
  FileNameChip,
  SummaryBar,
  ValidCount,
  InvalidCount,
  TotalCount,
  TableWrapper,
  Table,
  Th,
  Td,
  Tr,
  StatusIcon,
  ErrorBanner,
  ResultBanner,
  ResultIcon,
  ResultTitle,
  ResultSubtext,
  ModalFooter,
  FooterRight,
  LoadingState,
} from '../shared/BulkUploadShared';
import { downloadErrorsCsv } from '../shared/bulkUploadUtils';
import { goalTemplateHeaders, downloadGoalImportTemplate } from './goalImportTemplate';

// ============================================================================
// Constants
// ============================================================================

const CONFLICT_OUTCOMES = new Set(['UPDATE', 'REINTRODUCE']);
const ERROR_OUTCOMES = new Set(['ERROR', 'UNMATCHED', 'REVIEW']);
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB — aligned with the API's server-side cap

const OUTCOME_LABEL: Record<string, string> = {
  NEW: 'New',
  UPDATE: 'Update',
  REINTRODUCE: 'Reintroduce',
  UNCHANGED: 'Unchanged',
  SKIP: 'Skip',
  REVIEW: 'Review',
  UNMATCHED: 'Unmatched',
  ERROR: 'Error',
};

const defaultResolutionFor = (row: GoalImportRow): GoalConflictResolution =>
  row.outcome === 'REINTRODUCE'
    ? { action: 'REINTRODUCE', reintroduceMode: 'REACTIVATE' }
    : { action: 'UPDATE' };

/**
 * The execute endpoint applies a server-side cached validate plan (30-min TTL).
 * If the cache has expired (or the request lands on a different replica), it
 * returns 400 with this message — distinct from a generic execute failure
 * because the fix is "re-validate", not "retry the same request".
 */
const isImportSessionExpiredError = (err: unknown): boolean => {
  const response = (err as { response?: { status?: number; data?: { message?: unknown } } } | null)
    ?.response;
  if (response?.status !== 400) return false;
  const msg = response.data?.message;
  const text = typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join(' ') : '';
  return /import session expired/i.test(text);
};

const handleDownloadErrors = (rows: GoalImportRow[], mode: 'single' | 'multi') => {
  downloadErrorsCsv(
    rows.map((r) => ({
      row: r.rowNumber,
      client_external_id: r.clientExternalId,
      goal_key: r.goalKey,
      parent_goal_key: r.parentGoalKey ?? '',
      domain: r.domain ?? '',
      program: r.program ?? '',
      goal: r.goal ?? '',
      target: r.target ?? '',
      status: r.status,
      date_initiated: r.dateInitiated ?? '',
      date_met: r.dateMet ?? '',
      reason: r.reason ?? '',
      valid: !ERROR_OUTCOMES.has(r.outcome),
    })),
    goalTemplateHeaders(mode),
    'goals-import-errors.csv',
  );
};

// ============================================================================
// Styled
// ============================================================================

const UploadActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const OutcomeIcon = styled.span<{ $outcome: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme, $outcome }) =>
    $outcome === 'ERROR' || $outcome === 'UNMATCHED'
      ? theme.colors.status.error.text
      : $outcome === 'REVIEW'
        ? theme.colors.status.warning.text
        : $outcome === 'NEW'
          ? theme.colors.status.success.text
          : theme.colors.status.info.text};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const BulkApplyBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  flex-wrap: wrap;
`;

const BulkApplyLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ResolutionSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  background: ${({ theme }) => theme.colors.backgrounds.white};
`;

const ResolutionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WarningNote = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.warning.text};

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SummaryReconcile = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: ${({ theme }) => theme.spacing[2]} 0 0;
`;

const PreviewTable = styled(Table)`
  min-width: 900px;
`;

// ============================================================================
// Component
// ============================================================================

type Step = 'upload' | 'preview' | 'resolve' | 'result';

interface GoalImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 'single' scopes the import to one client; 'multi' fans out by client_external_id per row. */
  mode: 'single' | 'multi';
  clientId?: string;
  /** Overlay z-index — raise this when opened on top of another high-z overlay (e.g. a drawer). */
  zIndex?: number;
}

export const GoalImportModal = ({
  isOpen,
  onClose,
  mode,
  clientId,
  zIndex,
}: GoalImportModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [resolutions, setResolutions] = useState<Record<number, GoalConflictResolution>>({});

  const validateMutation = useGoalImportValidate();
  const executeMutation = useGoalImportExecute(mode === 'single' ? clientId : undefined);

  const validateResult = validateMutation.data;
  const conflicts = validateResult?.conflicts ?? [];
  const rows = useMemo(() => validateResult?.rows ?? [], [validateResult]);
  const errorRows = useMemo(() => rows.filter((r) => ERROR_OUTCOMES.has(r.outcome)), [rows]);

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      setUploadError('Please upload a .csv file.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size exceeds 2 MB. Please upload a smaller file.');
      return;
    }
    setUploadError(null);
    setSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleValidate = () => {
    if (!selectedFile) return;
    validateMutation.mutate(
      { file: selectedFile, clientId: mode === 'single' ? clientId : undefined },
      {
        onSuccess: (result) => {
          const initialResolutions: Record<number, GoalConflictResolution> = {};
          result.conflicts.forEach((row) => {
            initialResolutions[row.rowNumber] = defaultResolutionFor(row);
          });
          setResolutions(initialResolutions);
          setStep(result.conflicts.length > 0 ? 'resolve' : 'preview');
        },
        onError: () => setUploadError('Failed to validate the CSV file. Please check the format and try again.'),
      },
    );
  };

  const handleExecute = () => {
    const resolutionInput = conflicts.map((row) => ({
      rowNumber: row.rowNumber,
      resolution: resolutions[row.rowNumber] ?? defaultResolutionFor(row),
    }));
    executeMutation.mutate(
      { resolutions: resolutionInput, clientId: mode === 'single' ? clientId : undefined },
      {
        onSuccess: () => setStep('result'),
        onError: (err) => {
          if (isImportSessionExpiredError(err)) {
            // The validated plan aged out server-side — the file is still selected, so
            // send the user back to upload/re-validate rather than a dead-end retry.
            setStep('upload');
            setUploadError('Your import preview expired. Please re-validate the file before importing.');
          } else {
            setUploadError('Failed to import goals. Please try again.');
          }
        },
      },
    );
  };

  const handleClose = () => {
    setStep('upload');
    setSelectedFile(null);
    setUploadError(null);
    setResolutions({});
    validateMutation.reset();
    executeMutation.reset();
    onClose();
  };

  const setResolution = (rowNumber: number, resolution: GoalConflictResolution) => {
    setResolutions((prev) => ({ ...prev, [rowNumber]: resolution }));
  };

  const applyToAll = (action: GoalConflictResolution['action']) => {
    const resolution: GoalConflictResolution =
      action === 'REINTRODUCE' ? { action, reintroduceMode: 'REACTIVATE' } : { action };
    const next: Record<number, GoalConflictResolution> = {};
    conflicts.forEach((row) => {
      // Only bulk-apply REINTRODUCE to rows the engine actually flagged as reintroduce candidates.
      next[row.rowNumber] =
        action === 'REINTRODUCE' && row.outcome !== 'REINTRODUCE'
          ? (resolutions[row.rowNumber] ?? defaultResolutionFor(row))
          : resolution;
    });
    setResolutions(next);
  };

  const getTitle = () => {
    const scope = mode === 'single' ? 'Client' : 'Bulk';
    if (step === 'upload') return `Import Goals — ${scope} — Upload CSV`;
    if (step === 'preview') return `Import Goals — ${scope} — Preview`;
    if (step === 'resolve') return `Import Goals — ${scope} — Resolve Conflicts`;
    return `Import Goals — ${scope} — Complete`;
  };

  const summary = validateResult?.summary;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()} size="xl" zIndex={zIndex}>
      {step === 'upload' && (
        <>
          {uploadError && (
            <ErrorBanner>
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <DropZone
            $dragOver={dragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            aria-label="Upload CSV file"
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          >
            <UploadCloud />
            <DropLabel>Drop your CSV here or click to browse</DropLabel>
            <DropSubLabel>Normalized template: {goalTemplateHeaders(mode).join(', ')}</DropSubLabel>
            {selectedFile && <FileNameChip>{selectedFile.name}</FileNameChip>}
          </DropZone>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = '';
            }}
          />

          <UploadActions>
            <Button variant="ghost" leftIcon={<Download />} onClick={() => downloadGoalImportTemplate(mode)}>
              Download Template
            </Button>
          </UploadActions>

          {validateMutation.isPending && <LoadingState>Validating CSV...</LoadingState>}

          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                onClick={handleValidate}
                disabled={!selectedFile || validateMutation.isPending}
                isLoading={validateMutation.isPending}
              >
                Upload & Validate
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {(step === 'preview' || step === 'resolve') && validateResult && summary && (
        <>
          {uploadError && (
            <ErrorBanner>
              <AlertTriangle />
              {uploadError}
            </ErrorBanner>
          )}

          <SummaryBar>
            <ValidCount>
              <CheckCircle />
              {summary.new} new
            </ValidCount>
            <ValidCount>
              <CheckCircle />
              {summary.update} update
            </ValidCount>
            <ValidCount>
              <CheckCircle />
              {summary.reintroduce} reintroduce
            </ValidCount>
            <InvalidCount>
              <XCircle />
              {summary.unmatched} unmatched
            </InvalidCount>
            <TotalCount>out of {summary.totalRows} total</TotalCount>
          </SummaryBar>

          {step === 'resolve' && (
            <BulkApplyBar>
              <BulkApplyLabel>Apply to all similar conflicts:</BulkApplyLabel>
              <Button variant="ghost" size="sm" onClick={() => applyToAll('UPDATE')}>
                Update all
              </Button>
              <Button variant="ghost" size="sm" onClick={() => applyToAll('SKIP')}>
                Skip all
              </Button>
              <Button variant="ghost" size="sm" onClick={() => applyToAll('REINTRODUCE')}>
                Reintroduce all
              </Button>
            </BulkApplyBar>
          )}

          <ResponsiveTableWrapper>
            <TableWrapper>
              <PreviewTable>
                <thead>
                  <tr>
                    <Th>Row</Th>
                    <Th>Client ID</Th>
                    <Th>Domain</Th>
                    <Th>Program</Th>
                    <Th>Goal</Th>
                    <Th>Status</Th>
                    <Th>Outcome</Th>
                    <Th>Reason</Th>
                    {step === 'resolve' && <Th>Resolution</Th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isConflict = CONFLICT_OUTCOMES.has(row.outcome);
                    const isError = ERROR_OUTCOMES.has(row.outcome);
                    const resolution = resolutions[row.rowNumber];
                    return (
                      <Tr key={row.rowNumber} $invalid={isError}>
                        <Td $invalid={isError}>{row.rowNumber}</Td>
                        <Td $invalid={isError}>{row.clientExternalId}</Td>
                        <Td $invalid={isError}>{row.domain ?? '—'}</Td>
                        <Td $invalid={isError}>{row.program ?? '—'}</Td>
                        <Td $invalid={isError}>{row.goal ?? '—'}</Td>
                        <Td $invalid={isError}>{row.status}</Td>
                        <Td>
                          <StatusIcon $valid={!isError}>
                            <OutcomeIcon $outcome={row.outcome}>
                              {isError ? <XCircle /> : <CheckCircle />}
                              {OUTCOME_LABEL[row.outcome] ?? row.outcome}
                            </OutcomeIcon>
                          </StatusIcon>
                        </Td>
                        <Td $invalid={isError}>{row.reason ?? '—'}</Td>
                        {step === 'resolve' && (
                          <Td>
                            {isConflict ? (
                              <ResolutionCell>
                                <ResolutionSelect
                                  value={resolution?.action ?? 'UPDATE'}
                                  onChange={(e) =>
                                    setResolution(row.rowNumber, {
                                      action: e.target.value as GoalConflictResolution['action'],
                                      reintroduceMode:
                                        e.target.value === 'REINTRODUCE' ? 'REACTIVATE' : undefined,
                                    })
                                  }
                                  aria-label={`Resolution for row ${row.rowNumber}`}
                                >
                                  <option value="UPDATE">Update</option>
                                  <option value="SKIP">Skip</option>
                                  <option value="REINTRODUCE">Reintroduce</option>
                                </ResolutionSelect>
                                {resolution?.action === 'REINTRODUCE' && (
                                  <ResolutionSelect
                                    value={resolution.reintroduceMode ?? 'REACTIVATE'}
                                    onChange={(e) =>
                                      setResolution(row.rowNumber, {
                                        action: 'REINTRODUCE',
                                        reintroduceMode: e.target.value as 'REACTIVATE' | 'NEW_PHASE',
                                      })
                                    }
                                    aria-label={`Reintroduce mode for row ${row.rowNumber}`}
                                  >
                                    <option value="REACTIVATE">Reactivate</option>
                                    <option value="NEW_PHASE">New phase</option>
                                  </ResolutionSelect>
                                )}
                                {row.reason?.toLowerCase().includes('near-match') && (
                                  <WarningNote>
                                    <AlertCircle /> Possible typo of an existing label
                                  </WarningNote>
                                )}
                              </ResolutionCell>
                            ) : (
                              '—'
                            )}
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                </tbody>
              </PreviewTable>
            </TableWrapper>
          </ResponsiveTableWrapper>

          <SummaryReconcile>
            Rows in ({summary.totalRows}) = New ({summary.new}) + Update ({summary.update}) +
            Reintroduce ({summary.reintroduce}) + Unchanged ({summary.unchanged}) + Unmatched (
            {summary.unmatched})
          </SummaryReconcile>

          <ModalFooter>
            <Button variant="secondary" onClick={() => setStep('upload')}>
              Back
            </Button>
            <FooterRight>
              {errorRows.length > 0 && (
                <Button variant="ghost" leftIcon={<Download />} onClick={() => handleDownloadErrors(errorRows, mode)}>
                  Download Errors
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleExecute}
                disabled={executeMutation.isPending}
                isLoading={executeMutation.isPending}
              >
                Import Goals
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {step === 'result' && executeMutation.data && (
        <>
          <ResultBanner>
            <ResultIcon>
              <CheckCircle />
            </ResultIcon>
            <ResultTitle>Goals imported!</ResultTitle>
            <ResultSubtext>
              {executeMutation.data.new} new, {executeMutation.data.updated} updated,{' '}
              {executeMutation.data.reintroduced} reintroduced, {executeMutation.data.unchanged}{' '}
              unchanged, {executeMutation.data.unmatched} unmatched.
            </ResultSubtext>
            {executeMutation.data.errors.length > 0 && (
              <ResultSubtext>
                {executeMutation.data.errors.length} row
                {executeMutation.data.errors.length !== 1 ? 's' : ''} failed to import.
              </ResultSubtext>
            )}
          </ResultBanner>

          <ModalFooter>
            <div />
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default GoalImportModal;
